/**
 * V4CommandPalette — Phase 6c-3.
 *
 * Universal quick-action picker bound to ⌘K / Ctrl+K. Combines three
 * categories of actions into one searchable surface so operators can:
 *
 *   • Insert any node type by typing its name      (replaces palette scroll)
 *   • Switch to another workflow                    (replaces dropdown click)
 *   • Run any common action: save, save-as, undo,  (replaces toolbar hunting)
 *     redo, re-layout, toggle validation panel
 *
 * Design choices:
 *   • Single fuzzy filter over the union — categories are organisational,
 *     not filters. Type "save" → see Save + Save As; type "ht" → see HTTP
 *     Request + Human Task; etc.
 *   • Modal-centric layout (centered overlay) — same convention as Notion,
 *     Linear, VS Code. Operators already know it.
 *   • Keyboard-first: ↑/↓ navigate, Enter executes, Esc closes.
 *   • Mouse works too: hover sets active, click executes.
 *   • Self-contained — accepts callbacks + data, owns no app state. Easy
 *     to drop in elsewhere later (e.g. Connectors page) by passing
 *     different items.
 *
 * Host wiring (CanvasPageV4):
 *   • Owns the open/close state.
 *   • Registers the ⌘K hotkey via the existing keyboard handler.
 *   • Builds the actions[] from canvas + handlers + node registry.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ArrowRight, Hash, Workflow, Zap } from 'lucide-react';

const KIND_META = {
  action:   { label: 'Actions',          icon: Zap,      tone: '#0d9488' },
  insert:   { label: 'Insert Node',      icon: Hash,     tone: '#0d9488' },
  workflow: { label: 'Switch Workflow',  icon: Workflow, tone: '#0ea5e9' },
};

const KIND_ORDER = ['action', 'insert', 'workflow'];

export default function V4CommandPalette({ open, onClose, items }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset state on every open. Otherwise reopening shows the last query
  // and a stale active index — common surprise in this UX.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // Defer focus until the input is actually mounted in the DOM.
      requestAnimationFrame(() => {
        if (inputRef.current) inputRef.current.focus();
      });
    }
  }, [open]);

  // Fuzzy match: every char of the query must appear in order in the
  // candidate. Score = how tightly packed those chars are; tighter
  // matches rank higher. Falls back to substring containment for one-
  // character queries (which subsequence-matches everything).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    const scored = [];
    for (const it of items) {
      const hay = (it.label + ' ' + (it.subtitle || '') + ' ' + (it.keywords || []).join(' ')).toLowerCase();
      const score = fuzzyScore(hay, q);
      if (score > 0) scored.push({ it, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.map((s) => s.it);
  }, [items, query]);

  // Group by kind for the rendered list. Keeps category headers in a
  // deterministic order regardless of how the items array was built.
  const groups = useMemo(() => {
    const byKind = {};
    for (const it of filtered) {
      const k = it.kind || 'action';
      if (!byKind[k]) byKind[k] = [];
      byKind[k].push(it);
    }
    return KIND_ORDER
      .map((k) => ({ kind: k, items: byKind[k] || [] }))
      .filter((g) => g.items.length > 0);
  }, [filtered]);

  // Flat list for keyboard nav. Order matches what the user sees
  // top-to-bottom.
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // Clamp active index when filtered list shrinks (otherwise typing
  // narrows the list but the active row points off the end).
  useEffect(() => {
    if (active >= flat.length) setActive(Math.max(0, flat.length - 1));
  }, [flat.length, active]);

  if (!open) return null;

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flat[active];
      if (target && typeof target.run === 'function') {
        target.run();
        onClose();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '10vh',
      }}
    >
      <div style={{
        width: 'min(640px, 92vw)',
        background: '#ffffff',
        borderRadius: 12,
        boxShadow: '0 24px 56px -12px rgba(15, 23, 42, 0.35), 0 4px 12px -2px rgba(15, 23, 42, 0.15)',
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        maxHeight: '70vh',
      }}>
        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 14px',
          borderBottom: '0.5px solid #e2e8f0',
        }}>
          <Search size={14} aria-hidden="true" style={{ color: '#94a3b8' }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onKey}
            placeholder="Type to search actions, nodes, workflows…"
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1,
              border: 'none', outline: 'none',
              fontSize: 14, color: '#0f172a',
              background: 'transparent',
              fontFamily: 'inherit',
            }}
          />
          <kbd style={kbdStyle}>esc</kbd>
        </div>

        {/* Results body */}
        <div ref={listRef} style={{ overflowY: 'auto', flex: 1 }}>
          {flat.length === 0 ? (
            <div style={{
              padding: 24, textAlign: 'center',
              fontSize: 12, color: '#64748b',
            }}>
              No matches for &ldquo;{query}&rdquo;.
            </div>
          ) : (
            groups.map(({ kind, items: groupItems }) => {
              const meta = KIND_META[kind] || KIND_META.action;
              const Icon = meta.icon;
              return (
                <section key={kind}>
                  <header style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px 4px',
                    fontSize: 10, fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}>
                    <Icon size={11} aria-hidden="true" style={{ color: meta.tone }} />
                    {meta.label}
                    <span style={{ marginLeft: 'auto', fontWeight: 500, color: '#94a3b8' }}>
                      {groupItems.length}
                    </span>
                  </header>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {groupItems.map((it) => {
                      const idx = flat.indexOf(it);
                      const isActive = idx === active;
                      return (
                        <li key={it.id}>
                          <button
                            type="button"
                            onMouseEnter={() => setActive(idx)}
                            onClick={() => { if (typeof it.run === 'function') { it.run(); onClose(); } }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              width: '100%',
                              padding: '8px 14px',
                              border: 'none',
                              background: isActive ? '#f1f5f9' : 'transparent',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontFamily: 'inherit',
                            }}
                          >
                            {it.iconChar && (
                              <span style={{
                                width: 18, textAlign: 'center',
                                fontSize: 12, color: '#475569',
                              }}>{it.iconChar}</span>
                            )}
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{
                                fontSize: 13, color: '#0f172a',
                                display: 'block',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>{it.label}</span>
                              {it.subtitle && (
                                <span style={{
                                  fontSize: 10.5, color: '#64748b',
                                  display: 'block',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>{it.subtitle}</span>
                              )}
                            </span>
                            {it.shortcut && (
                              <kbd style={kbdStyle}>{it.shortcut}</kbd>
                            )}
                            {isActive && (
                              <ArrowRight size={12} aria-hidden="true" style={{ color: '#64748b' }} />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })
          )}
        </div>

        {/* Footer hints */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '8px 14px',
          borderTop: '0.5px solid #e2e8f0',
          background: '#f8fafc',
          fontSize: 10, color: '#64748b',
        }}>
          <span><kbd style={kbdStyle}>↑↓</kbd> navigate</span>
          <span><kbd style={kbdStyle}>↵</kbd> run</span>
          <span><kbd style={kbdStyle}>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

/* ───── fuzzy match scorer ────────────────────────────────────────── */
// Returns 0 for "no match", higher = better. Cheap subsequence match
// with proximity-bonus: characters that match closer together (and
// closer to the start) score higher. Good enough for a 200-item pool.
function fuzzyScore(haystack, needle) {
  let score = 0;
  let lastIdx = -1;
  let consecutive = 0;
  for (const ch of needle) {
    const idx = haystack.indexOf(ch, lastIdx + 1);
    if (idx === -1) return 0;
    // Base points for the match
    score += 1;
    // Bonus for matches at the start
    if (idx === 0) score += 4;
    // Bonus for consecutive matches (typing in-order substrings)
    if (lastIdx >= 0 && idx === lastIdx + 1) {
      consecutive += 1;
      score += consecutive * 2;
    } else {
      consecutive = 0;
    }
    // Penalty for big gaps (1pt per char skipped, capped)
    if (lastIdx >= 0) {
      const gap = idx - lastIdx - 1;
      score -= Math.min(gap, 5);
    }
    lastIdx = idx;
  }
  return score;
}

const kbdStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '1px 5px',
  fontSize: 9.5, fontWeight: 600,
  color: '#475569', background: '#e2e8f0',
  border: 'none',
  borderRadius: 3,
  fontFamily: 'inherit',
  lineHeight: 1.4,
};
